import { createRouter, createWebHistory } from 'vue-router'
import { pageview } from '@/lib/ga'

import HomeLanding from '@/views/HomeLanding.vue'
import PricingPage from '@/views/PricingPage.vue'
import BillingSuccess from '@/views/BillingSuccess.vue'
import BillingCancel from '@/views/BillingCancel.vue'
import LoginPage from '@/views/LoginPage.vue'
import DashboardPage from '@/views/DashboardPage.vue'
import SubscriptionManage from '@/views/SubscriptionManage.vue'
import PanelInicio from '@/components/dashboard/PanelInicio.vue'
import GalleryView from '@/views/GalleryView.vue'
import PanelColors from '@/components/dashboard/PanelColors.vue'
import NotFound from '@/views/NotFound.vue'
import StudioProjects from '@/components/dashboard/StudioProjects.vue'
import PanelStudio from '@/components/dashboard/PanelStudio.vue'
import PalettesPage from '@/views/PalettesPage.vue'
import PanelTester from '@/components/dashboard/PanelTester.vue'
import PaletteStudioCreator from '@/components/dashboard/PaletteStudioCreator.vue'
import SemanticTokensPanel from '@/components/dashboard/SemanticTokensPanel.vue'
import ContrastAccessibilityPanel from '@/components/dashboard/ContrastAccessibilityPanel.vue'
import LegalConsentPage from '@/components/termos/LegalConsentPage.vue'
import TermsOfUsePage from '@/components/termos/TermsOfUsePage.vue'
import PrivacyPolicyPage from '@/components/termos/PrivacyPolicyPage.vue'
import DataProtectionAgreementPage from '@/components/termos/DataProtectionAgreementPage.vue'
import DocsPage from '@/components/termos/DocsPage.vue'
import NexgridColorAI from '@/components/dashboard/NexgridColorAI.vue'
import AgenteAi from '@/components/dashboard/AgenteAi.vue'
import ResourceHub from '@/components/dashboard/ResourceHub.vue'
import TextureProjects from '@/components/dashboard/TextureProjects.vue'
import TextureStudio from '@/components/dashboard/TextureStudio.vue'

export const API = 'https://backend.nexgridco.com'

const dashboardChildren = [
  {
    path: '',
    name: 'inicio',
    component: PanelInicio,
  },

  {
    path: 'subscription',
    name: 'assinatura',
    component: SubscriptionManage,
  },

  {
    path: 'gallery/:gid?',
    name: 'gallery',
    component: GalleryView,
    props: true,
  },

  {
    path: 'galeria/:gid?',
    name: 'galeria',
    component: GalleryView,
    props: true,
  },

  {
    path: 'colors',
    name: 'cores',
    component: PanelColors,
  },

  {
    path: 'studio',
    name: 'studio',
    component: StudioProjects,
  },

  {
    path: 'studio/:projectId',
    name: 'studio-project',
    component: PanelStudio,
    props: true,
  },

  {
    path: 'tester',
    name: 'studio-tester',
    component: PanelTester,
  },

  {
    path: 'create',
    name: 'create',
    component: PaletteStudioCreator,
  },

  {
    path: 'semantic',
    name: 'semantic',
    component: SemanticTokensPanel,
  },

  {
    path: 'accessibility',
    name: 'accessibility',
    component: ContrastAccessibilityPanel,
  },

  {
    path: 'iacolor',
    name: 'iacolor',
    component: NexgridColorAI,
  },

  {
    path: 'ask',
    name: 'ask',
    component: AgenteAi,
  },
  {
    path: 'texture',
    name: 'texture',
    component: TextureProjects,
  },

  {
    path: 'texture/:projectId',
    name: 'texture-project',
    component: TextureStudio,
    props: true,
  },

  {
    path: 'hub',
    name: 'hub',
    component: ResourceHub,
  },
]

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeLanding,
  },

  {
    path: '/app/palettes',
    name: 'palettes',
    component: PalettesPage,
  },

  {
    path: '/pricing',
    name: 'pricing',
    component: PricingPage,
  },

  {
    path: '/billing/success',
    name: 'billing-success',
    component: BillingSuccess,
  },

  {
    path: '/billing/cancel',
    name: 'billing-cancel',
    component: BillingCancel,
  },

  {
    path: '/play',
    name: 'entrar',
    component: LoginPage,
    meta: {
      guest: true,
    },
  },

  {
    path: '/dashboard',
    component: DashboardPage,
    meta: {
      auth: true,
    },
    children: dashboardChildren,
  },

  {
    path: '/legal/consent',
    name: 'legal-consent',
    component: LegalConsentPage,
    meta: {
      auth: true,
      skipLegalCheck: true,
    },
  },

  {
    path: '/legal/terms',
    name: 'terms',
    component: TermsOfUsePage,
  },

  {
    path: '/legal/privacy',
    name: 'privacy',
    component: PrivacyPolicyPage,
  },

  {
    path: '/legal/dpa',
    name: 'dpa',
    component: DataProtectionAgreementPage,
  },

  {
    path: '/docs',
    name: 'docs',
    component: DocsPage,
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'notfound',
    component: NotFound,
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,

  scrollBehavior: () => ({
    top: 0,
  }),
})

router.beforeEach(async (to) => {
  const token =
    localStorage.getItem('token') ||
    sessionStorage.getItem('token')

  const requiresAuth = to.matched.some(
    (r) => r.meta?.auth,
  )

  const isGuestOnly = to.matched.some(
    (r) => r.meta?.guest,
  )

  const skipLegalCheck = to.matched.some(
    (r) => r.meta?.skipLegalCheck,
  )

  if (requiresAuth) {
    if (!token) {
      return {
        name: 'entrar',
        query: {
          next: to.fullPath,
        },
      }
    }

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {}

    const s = await fetch(
      `${API}/billing/status`,
      {
        credentials: 'include',
        headers,
      },
    )

    if (s.status === 401) {
      return {
        name: 'entrar',
        query: {
          next: to.fullPath,
        },
      }
    }

    const st = await s.json()

    if (st.hasActive) {
      if (
        !st.hasAcceptedLegal &&
        !skipLegalCheck
      ) {
        return {
          name: 'legal-consent',

          query: {
            next:
              to.fullPath ||
              '/dashboard',
          },
        }
      }

      return true
    }

    const e = await fetch(
      `${API}/billing/ensure`,
      {
        method: 'POST',

        credentials: 'include',

        headers: {
          'Content-Type':
            'application/json',

          ...headers,
        },

        body: JSON.stringify({}),
      },
    )

    if (e.ok) {
      const j = await e.json()

      if (j.url) {
        window.location.href = j.url

        return false
      }
    }

    return {
      name: 'pricing',
    }
  }

  if (isGuestOnly && token) {
    return {
      name: 'inicio',
    }
  }

  return true
})

router.afterEach((to) => {
  pageview(to.fullPath)
})

export default router